<template lang="pug">
#send-document-template-list(v-cloak)
  .box
    .box-header.register-header.d-flex.flex-row
      .d-flex.flex-column
        .title
          span 文書送付のテンプレート
        .note
          span 文書送付で使用するテンプレート（タイトル、本文、オプション）を登録できます。
          br
          span 登録したテンプレートは、文書送付時に選択することが出来ます。
      .flex-fill.d-flex.flex-row-reverse
        button.btn.back-btn(@click="gotoSendDocument" type="button") 戻る
        button.btn.add-btn(@click="gotoSendDocumentTemplateRegister") 追加

    .box-body
      SendDocumentTemplateTable(
        v-model="items"
        :fields="fields"
        :totalRows.sync="rows"
        :currentPage="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        @onDetail="showTemplateDetailModal"
        @onEdit="gotoSendDocumentTemplateEdit"
        @onDelete="showConfirmDeleteModal"
      )
      .table-options.d-flex.flex-row.justify-content-end.align-items-center
        // ページあたりの行数
        span.table-option-text 1ページの行数:
        .per-page
          b-form-select(
            id="per-page-select"
            v-model="perPage"
            :options="pageOptions"
            size="md"
            @change="selectPerPage"
          )
        // ページネーション
        b-pagination.m-0(
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          :disabled="isLoading"
          @change="selectPage"
          hide-goto-end-buttons
        )

  SendDocumentTemplateDetailModal(v-model="showDetailModal" :template="detailTargetTemplate")

  OkCancelModal(v-model="showDeleteModal" :title="deleteMessage" @ok="deleteSendDocumentTemplate")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。" @ok="createView")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";

import SendDocumentTemplatesService, { SendDocumentTemplatesGetModel, TemplateType } from "@/commons/services/SendDocumentTemplates";
import SendDocumentCategoriesService, { SendDocumentCategoryGetModel } from "@/commons/services/SendDocumentCategories";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import SendDocumentTemplateTable, { SendDocumentTemplateTableField, SendDocumentTemplateTableItem } from "@/pc/containers/sendDocuments/SendDocumentTemplateTable.vue";
import SendDocumentTemplateDetailModal from "@/pc/containers/sendDocuments/SendDocumentTemplateDetailModal.vue";

/**
 * ### 文書送付テンプレート一覧画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    SendDocumentTemplateTable,
    SendDocumentTemplateDetailModal
  }
})
export default class SendDocumentGroupList extends Vue {
  /** 文書送付テンプレートサービス */
  @Inject("SendDocumentTemplatesService") sendDocumentTemplatesService!: SendDocumentTemplatesService;
  /** 文書送付カテゴリーサービス */
  @Inject("SendDocumentCategoriesService") categoriesService!: SendDocumentCategoriesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 詳細モーダル表示可否 */
  showDetailModal: boolean = false;
  /** 詳細モーダル表示内容 */
  detailTargetTemplate: SendDocumentTemplateTableItem | null = null;

  /** 削除確認モーダル表示可否 */
  showDeleteModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;
  /** 削除対象の文書送付テンプレートID */
  deleteTargetId: number = 0;
  /** 削除モーダルに表示するメッセージ */
  deleteMessage: string = "";

  /** カテゴリー名のMAP key:カテゴリーID value:カテゴリー名 */
  categoryNameMap: Map<number, string> = new Map<number, string>();

  /** 文書送付テンプレート列名 */
  fields: Array<SendDocumentTemplateTableField> = [];
  /** 文書送付テンプレート各行の情報 */
  items: Array<SendDocumentTemplateTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.sendDocumentTemplate.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.sendDocumentTemplate.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.sendDocumentTemplate.pageRange;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `send-document-template-list-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * ### vue lifecycle mounted
   *
   * - 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<SendDocumentTemplateTableField> = this.createFields();
    this.fields = fields;

    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.sendDocumentTemplate.perPage);

    // 動的に変更する画面情報を作成する
    this.items = [];
    this.currentRows = this.items.length;

    await this.createView();

    return;
  }

  /**
   * ### 文書送付テンプレートの列名を作成する
   */
  public createFields(): Array<SendDocumentTemplateTableField> {
    const fields: Array<SendDocumentTemplateTableField> = [];

    fields.push({ key: "name", label: "テンプレート名", sortable: false, sortDirection: "desc", class: "name" });
    fields.push({ key: "title", label: "タイトル", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "writer", label: "記載者", sortable: false, sortDirection: "desc", class: "writer" });
    fields.push({ key: "operation", label: "操作", sortable: false, sortDirection: "desc", class: "operation text-center" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["send-document-template-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // カテゴリー名変換のためにカテゴリーを取得する
    const sendDocumentCategories: Array<SendDocumentCategoryGetModel> = await this.getSendDocumentCategories();
    const categoryNameMap: Map<number, string> = this.createCategoryNameMap(sendDocumentCategories);
    this.categoryNameMap = categoryNameMap;

    // 文書送付テンプレートテーブルに渡す各プロパティを作成する
    const tempaltes: Array<SendDocumentTemplatesGetModel> = await this.getSendDocumentTemplates();
    const filterdTemplates: Array<SendDocumentTemplatesGetModel> = this.filteringByTemplateType(tempaltes);
    const items: Array<SendDocumentTemplateTableItem> = this.createItems(filterdTemplates, categoryNameMap);
    this.items = items;
    this.currentRows = items.length;
    this.currentPage = 1;

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const tempaltes: Array<SendDocumentTemplatesGetModel> = await this.getSendDocumentTemplates();
      const filterdTemplates: Array<SendDocumentTemplatesGetModel> = this.filteringByTemplateType(tempaltes);
      this.rows = filterdTemplates.length;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 文書送付カテゴリーを取得する
   */
  public async getSendDocumentCategories(): Promise<Array<SendDocumentCategoryGetModel>> {
    const response: Array<SendDocumentCategoryGetModel> = await this.categoriesService.gets(userStore);
    return response;
  }

  /**
   * ### 文書送付カテゴリー名のMAPを作成する
   */
  public createCategoryNameMap(sendDocumentCategories: Array<SendDocumentCategoryGetModel>): Map<number, string> {
    const categoryNameMap: Map<number, string> = new Map<number, string>();
    for (const sendDocumentCategory of sendDocumentCategories) {
      categoryNameMap.set(sendDocumentCategory.id, sendDocumentCategory.name);
    }
    return categoryNameMap;
  }

  /**
   * ### 文書送付テンプレートを取得する
   */
  public async getSendDocumentTemplates(): Promise<Array<SendDocumentTemplatesGetModel>> {
    const records: Array<SendDocumentTemplatesGetModel> = await this.sendDocumentTemplatesService.gets(userStore);
    return records;
  }

  /**
   * ### 文書送付テンプレートをテンプレート種別でフィルタリングする
   *
   * - テンプレート種別が「家族連絡サービス」のもののみ返却する
   */
  public filteringByTemplateType(templates: Array<SendDocumentTemplatesGetModel>): Array<SendDocumentTemplatesGetModel> {
    return templates.concat().filter((temaplte) => {
      return temaplte.templateType == TemplateType.FAMILY_SERVICE ? true : false;
    });
  }

  /**
   * ### 文書送付テンプレートの行を作成する
   */
  public createItems(sendDocumentTemplates: Array<SendDocumentTemplatesGetModel>, categoryNameMap: Map<number, string>): Array<SendDocumentTemplateTableItem> {
    const items: Array<SendDocumentTemplateTableItem> = [];
    for (const template of sendDocumentTemplates) {
      const categoryName: string | undefined = categoryNameMap.get(template.categoryId);
      items.push({
        ...template,
        categoryName: categoryName ? categoryName : ""
      });
    }
    return items;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.items = [];
    this.currentRows = this.items.length;
    await this.createView();
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 文書送付画面へ遷移
   */
  public gotoSendDocument(): void {
    this.$router.push(PcUrl.SendDocumentList);
    return;
  }

  /**
   * ### 文書送付テンプレート登録画面へ遷移
   */
  public gotoSendDocumentTemplateRegister(): void {
    this.$router.push(PcUrl.SendDocumentTemplateRegister);
    return;
  }

  /**
   * ### 詳細モーダルを表示する
   */
  public showTemplateDetailModal(template: SendDocumentTemplateTableItem): void {
    this.detailTargetTemplate = template;
    this.showDetailModal = true;
    return;
  }

  /**
   * ### 文書送付テンプレート編集画面へ遷移
   */
  public gotoSendDocumentTemplateEdit(template: SendDocumentTemplateTableItem): void {
    this.$router.push({ name: PcUrl.SendDocumentTemplateEdit, params: { id: `${template.id}` } });
    return;
  }

  /**
   * ### 削除確認モーダルを表示する
   *
   * - 削除メッセージは動的に作成する
   */
  public showConfirmDeleteModal(template: SendDocumentTemplateTableItem): void {
    const name: string = template.name ? template.name : "";
    this.deleteMessage = `${name}を削除しますか？`;
    this.showDeleteModal = true;

    this.deleteTargetId = template.id;

    return;
  }

  /**
   * ### 文書送付テンプレートを削除する
   */
  public async deleteSendDocumentTemplate(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.sendDocumentTemplatesService.delete(userStore, this.deleteTargetId);

    // 削除成功ダイアログを表示する
    this.showDeleteSuccessModal = true;
    this.isBusy = false;
    await this.createView();

    return;
  }
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#send-document-template-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .add-btn,
  .back-btn {
    width: 80px;
  }
  .add-btn {
    margin-right: 12px;
  }
}
</style>
